import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'

import { Icon, Text, Toggle } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import type {
  SeparationQuestion,
  SeparationSubItem,
} from '@/features/onboarding/separation'
import { BOOKS_QUESTION } from './booksQuestion.styles'
import { plaidCategoryLabel } from '../shared/plaidCategory'

const DetailLine = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
      {label}
    </Text>
    <Text
      variant={TypographyStyles.BODY_SM}
      className="[overflow-wrap:anywhere]"
    >
      {value}
    </Text>
  </div>
)

const ExpandToggle = ({
  expanded,
  label,
  onToggle,
}: {
  expanded: boolean
  label: string
  onToggle: () => void
}) => (
  <button
    type="button"
    className={BOOKS_QUESTION.rowDetailToggle}
    aria-expanded={expanded}
    aria-label={`${expanded ? 'Hide' : 'Show'} ${label}`}
    onClick={onToggle}
  >
    <Icon
      icon={expanded ? faChevronUp : faChevronDown}
      color="gray"
      size="xs"
      backgroundClassName="size-auto"
    />
  </button>
)

interface TransactionRowProps {
  question: SeparationQuestion
  subItem: SeparationSubItem
  selection: string | undefined
  expanded: boolean
  needsAnswer: boolean
  onToggleExpanded: () => void
  onSelect: (value: string) => void
}

export const TransactionRow = ({
  question,
  subItem,
  selection,
  expanded,
  needsAnswer,
  onToggleExpanded,
  onSelect,
}: TransactionRowProps) => {
  const childTransactions = subItem.transactions ?? []
  const hasDetail =
    childTransactions.length > 0 ||
    [subItem.description, subItem.accountName, subItem.plaidCategory].some(
      Boolean
    )
  // Plaid often enriches the merchant to exactly the bank's wording; two identical lines
  // reads like a bug, so collapse them into one.
  const sameSource =
    Boolean(subItem.description) &&
    subItem.description?.trim().toLowerCase() ===
      subItem.merchantName?.trim().toLowerCase()
  const isTransactionLayout = question.subItemLayout === 'transaction'
  const togglesPair = question.subItemToggleOptions.length % 2 === 0

  return (
    <div
      className={mtc(
        BOOKS_QUESTION.transactionRow,
        needsAnswer && BOOKS_QUESTION.rowNeedsAnswer
      )}
    >
      <div className={BOOKS_QUESTION.transactionRowMain}>
        {isTransactionLayout ? (
          <div className="flex shrink-0 items-center max-sm:w-full max-sm:justify-between sm:order-1">
            <div className="w-[106px] shrink-0 p-2 max-sm:w-auto">
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {subItem.label}
              </Text>
            </div>
            <div className={BOOKS_QUESTION.transactionAmount}>
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                textAlign="right"
              >
                {subItem.meta}
              </Text>
            </div>
            {hasDetail && (
              <div className="max-sm:hidden">
                <ExpandToggle
                  expanded={expanded}
                  label="details"
                  onToggle={onToggleExpanded}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2 p-2 max-sm:w-full">
            <span
              className="h-4 w-4 shrink-0 rounded-full"
              style={{ backgroundColor: subItem.dotColor }}
            />
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
              className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
            >
              <span title={subItem.label}>{subItem.label}</span>
            </Text>
            {subItem.meta && (
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.MUTED}
                className="shrink-0 whitespace-nowrap"
              >
                {subItem.meta}
              </Text>
            )}
            {hasDetail && (
              <ExpandToggle
                expanded={expanded}
                label="transactions"
                onToggle={onToggleExpanded}
              />
            )}
          </div>
        )}
        {/* Each toggle is floored at its content width, so four don't fit beside the date
            on a narrow card. They go two by two there rather than wrapping three and one. */}
        <div
          className={mtc(
            'flex-1 flex-wrap gap-2 py-2 pr-2 pl-6 max-sm:flex-col max-sm:p-0 sm:order-2',
            togglesPair
              ? 'grid grid-cols-2 max-sm:flex @min-[640px]:flex'
              : 'flex'
          )}
        >
          {question.subItemToggleOptions.map((option) => (
            <Toggle
              key={option.value}
              variant="outline"
              size="sm"
              checked={selection === option.value}
              onCheckedChange={() => onSelect(option.value)}
              className={BOOKS_QUESTION.rowToggle}
            >
              {option.label}
            </Toggle>
          ))}
        </div>
        {/* Stacked, the disclosure has to sit against what it discloses. Above the toggles
            it is three full-width buttons away from the detail it opens, which at phone
            width can land off-screen and read as nothing having happened. */}
        {isTransactionLayout && hasDetail && (
          <div className="hidden max-sm:flex max-sm:w-full max-sm:justify-center">
            <ExpandToggle
              expanded={expanded}
              label="details"
              onToggle={onToggleExpanded}
            />
          </div>
        )}
      </div>

      {expanded && childTransactions.length > 0 && (
        <div className="flex flex-col gap-1.5 pt-2 pb-3">
          {childTransactions.map((child) => (
            <div key={child.id} className="flex justify-between gap-4 py-1">
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {child.label}
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {child.meta}
              </Text>
            </div>
          ))}
        </div>
      )}
      {expanded && childTransactions.length === 0 && hasDetail && (
        <div className="flex flex-col gap-1.5 pt-2 pb-3">
          {subItem.description && (
            <DetailLine
              label={
                sameSource ? 'Bank description / merchant' : 'Bank description'
              }
              value={subItem.description}
            />
          )}
          {subItem.merchantName && !sameSource && (
            <DetailLine label="Merchant" value={subItem.merchantName} />
          )}
          {subItem.accountName && (
            <DetailLine label="Account" value={subItem.accountName} />
          )}
          {subItem.plaidCategory && (
            <DetailLine
              label="Category"
              value={plaidCategoryLabel(subItem.plaidCategory)}
            />
          )}
        </div>
      )}
    </div>
  )
}
